import { Delete } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField
} from '@mui/material';

import { Field, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useFacultiesQuery } from 'api/hooks/useFacultiesQuery';
import StyledDropzone from 'app/components/Dropzone';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

export function CourseInformation() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { values, errors, setFieldValue, touched, setFieldTouched }: any = useFormikContext();

  const { data: faculties, isError } = useFacultiesQuery();

  useEffect(() => {
    if (isError) {
      dispatch(
        AlertActions.error(
          `${t(translations.pages.internationalOffice.courses.alerts.error.loadingCourses())}`
        )
      );
      navigate('/', { replace: true });
    }
  }, [dispatch, isError, navigate, t]);

  const facultySelectOptions: any[] = [];
  faculties.forEach(faculty => {
    facultySelectOptions.push({
      id: faculty.id,
      label: faculty.name_en || faculty.name,
      name: faculty.name_en || faculty.name
    });
  });
  facultySelectOptions.sort((a, b) =>
    a.label.toLowerCase() > b.label.toLowerCase()
      ? 1
      : a.label.toLowerCase() < b.label.toLowerCase()
      ? -1
      : 0
  );
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="orderNumber"
            error={touched.orderNumber && errors.orderNumber}
            helperText={touched.orderNumber && errors.orderNumber}
            fullWidth
            label={t(translations.pages.internationalOffice.courses.orderNumber())}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="courseNumber"
            error={touched.courseNumber && errors.courseNumber}
            helperText={touched.courseNumber && errors.courseNumber}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.courses.courseNumber())}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="courseName"
            error={touched.courseName && errors.courseName}
            helperText={touched.courseName && errors.courseName}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.courses.courseName())}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="courseOfficer"
            error={touched.courseOfficer && errors.courseOfficer}
            helperText={touched.courseOfficer && errors.courseOfficer}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.courses.courseOfficer())}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl required fullWidth error={touched.degree && errors.degree}>
            <InputLabel>{t(translations.pages.internationalOffice.courses.degree())}</InputLabel>
            <Select
              label={t(translations.pages.internationalOffice.courses.degree())}
              fullWidth
              value={values.degree || ''}
              onChange={e => setFieldValue('degree', e.target.value)}
            >
              <MenuItem key="Bachelor" value="Bachelor">
                Bachelor
              </MenuItem>
              <MenuItem key="Master" value="Master">
                Master
              </MenuItem>
            </Select>
            {touched.degree && errors.degree && <FormHelperText>{errors.degree}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="ectCount"
            error={touched.ectCount && errors.ectCount}
            helperText={touched.ectCount && errors.ectCount}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.courses.ects())}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl required fullWidth error={touched.faculty && errors.faculty}>
            <InputLabel>{t(translations.pages.internationalOffice.courses.faculty())}</InputLabel>
            <Select
              label={t(translations.pages.internationalOffice.courses.faculty())}
              fullWidth
              value={values.faculty || ''}
              onChange={e => setFieldValue('faculty', e.target.value)}
            >
              {facultySelectOptions.map(faculty => {
                return (
                  <MenuItem key={faculty.id} value={faculty.name}>
                    {faculty.label}
                  </MenuItem>
                );
              })}
            </Select>
            {touched.faculty && errors.faculty && <FormHelperText>{errors.faculty}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl
            className="role-detail-modal__body__input"
            required
            fullWidth
            error={touched.isWinter && errors.isWinter}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={values.isWinter}
                  onChange={v => {
                    setFieldTouched('isWinter');
                    setFieldValue('isWinter', v.target.checked);
                  }}
                  name="isWinter"
                  color="primary"
                />
              }
              label={t(translations.pages.internationalOffice.courses.isWinter())}
            />
            {touched.isWinter && errors.isWinter && (
              <FormHelperText>{errors.isWinter}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl
            className="role-detail-modal__body__input"
            required
            fullWidth
            error={touched.isSummer && errors.isSummer}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={values.isSummer}
                  onChange={v => {
                    setFieldTouched('isSummer');
                    setFieldValue('isSummer', v.target.checked);
                  }}
                  name="isSummer"
                  color="primary"
                />
              }
              label={t(translations.pages.internationalOffice.courses.isSummer())}
            />
            {touched.isSummer && errors.isSummer && (
              <FormHelperText>{errors.isSummer}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl
            className="role-detail-modal__body__input"
            required
            fullWidth
            error={touched.isAWPM && errors.isAWPM}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={values.isAWPM}
                  onChange={v => {
                    setFieldTouched('isAWPM');
                    setFieldValue('isAWPM', v.target.checked);
                  }}
                  name="isAWPM"
                  color="primary"
                />
              }
              label={t(translations.pages.internationalOffice.courses.isAWPM())}
            />
            {touched.isAWPM && errors.isAWPM && <FormHelperText>{errors.isAWPM}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <StyledDropzone
              name="courseDescription"
              setFieldValue={setFieldValue}
              value={values.courseDescription}
              errors={errors}
              accept={{ 'application/pdf': ['.pdf'] }}
              placeholder={
                values.courseDescription?.name ||
                values.courseDescription ||
                t(translations.pages.internationalOffice.courses.courseDescription())
              }
            />
          </Grid>
          {values.courseDescription && (
            <Grid item xs="auto" style={{ alignSelf: 'center' }}>
              <IconButton
                onClick={() => {
                  setFieldValue('courseDescription', null);
                }}
              >
                <Delete />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}
